import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  LayoutGrid,
  LayoutList,
  Paintbrush,
  Pencil,
  Plus,
  Power,
  Search,
  Trash2,
} from 'lucide-react';
import { Dialog } from '../reusable';
import API_BASE_URL from '../../config/api';
import { authFetch } from '../../services/apiClient';
import { useUIColors } from '../../hooks/useTheme';

const STATUS_STYLES = {
  Activo: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Inactivo: { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' },
  default: { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' },
};

const renderStatusBadge = (value) => {
  const status = String(value || 'Activo').trim() || 'Activo';
  const palette = STATUS_STYLES[status] || STATUS_STYLES.default;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '72px',
        padding: '3px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        backgroundColor: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
      }}
    >
      {status}
    </span>
  );
};

const normalizeColorDisplayName = (rawName = '') => {
  const input = String(rawName || '').trim();
  if (!input) return '';
  if (/\(s[íi]mil a\s+(ral|sw)\s*\d{3,4}\)/i.test(input)) return input;

  const ralMatch = input.match(/^RAL\s*(\d{4})\s*[-:]\s*(.+)$/i);
  if (ralMatch) {
    const ralCode = ralMatch[1];
    const baseName = String(ralMatch[2] || '').trim();
    return baseName ? `${baseName} (Símil a RAL ${ralCode})` : input;
  }

  const swMatch = input.match(/^(.+?)\s*[-(]?\s*SW\s*(\d{3,4})\)?$/i);
  if (swMatch) {
    const baseName = String(swMatch[1] || '').trim().replace(/[-(]\s*$/, '');
    const swCode = swMatch[2];
    return baseName ? `${baseName} (Símil a SW ${swCode})` : input;
  }

  return input;
};

/** Ruta relativa en BD (p. ej. products/BW01.webp) → URL servida por el backend local. */
const resolveProductImageUrl = (imagePath) => {
  const raw = String(imagePath || '').trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const relative = raw.replace(/^\/+/, '').replace(/^uploads\//, '');
  return `${API_BASE_URL}/uploads/${relative}`;
};

const MAX_PRODUCT_IMAGE_BYTES = 2 * 1024 * 1024;
const ALLOWED_PRODUCT_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const formatApiError = (detail) => {
  if (!detail) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || item?.message || String(item)).join('. ');
  }
  return String(detail);
};

const getProductFormatLabel = (row, formats) => {
  const ids = Array.isArray(row?.available_format_ids) ? row.available_format_ids : [];
  if (!ids.length) return '—';
  const labels = ids
    .map((id) => formats.find((f) => Number(f.id) === Number(id)))
    .filter(Boolean)
    .map((f) => f.format_code);
  return labels.length ? labels.join(', ') : '—';
};

const buildPreviewFullSku = (productSku, formatCode, colorCode) => {
  const parts = [productSku, formatCode, colorCode]
    .map((p) => String(p || '').trim().toUpperCase())
    .filter(Boolean);
  return parts.length === 3 ? parts.join('') : '';
};

const buildNextFormatCodePreview = (categoryCode, categoryFormats) => {
  const code = String(categoryCode || '').trim().toUpperCase();
  if (!code || code.length !== 1) return '';
  const maxCorr = (categoryFormats || []).reduce(
    (max, row) => Math.max(max, Number(row.category_corr) || 0),
    0
  );
  const nextCorr = maxCorr + 1;
  if (nextCorr < 1 || nextCorr > 26) return '';
  return `${code}${String.fromCharCode(64 + nextCorr)}`;
};

const formatCategoryLabel = (row) => {
  if (!row?.category_code) return '—';
  const name = row.category_name || row.category_code;
  return `${row.category_code} — ${name}`;
};

/** 1 galón de referencia = 4 litros (ej. costo 16 L ÷ 4 → costo/galón). */
const GALLON_VOLUME_LITERS = 4;

const parseFormatVolumeLiters = (formatName) => {
  const n = String(formatName || '').trim().toLowerCase();
  if (!n) return null;

  const literMatch = n.match(/(\d+(?:[.,]\d+)?)\s*l(?:itros?)?\b/);
  if (literMatch) return parseFloat(literMatch[1].replace(',', '.'));

  const mlMatch = n.match(/(\d+(?:[.,]\d+)?)\s*ml\b/);
  if (mlMatch) return parseFloat(mlMatch[1].replace(',', '.')) / 1000;

  if ((/1\/4|¼/.test(n) || n.includes('1/4')) && /gl|gal/.test(n)) {
    return GALLON_VOLUME_LITERS / 4;
  }
  if ((/1\/2|½/.test(n) || n.includes('1/2')) && /gl|gal/.test(n)) {
    return GALLON_VOLUME_LITERS / 2;
  }

  if (/gal[oó]n/.test(n)) return GALLON_VOLUME_LITERS;

  return null;
};

const formatCostCl = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return `$ ${n.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
};

const computeGallonCostRange = (productId, costs, formats) => {
  const formatById = new Map(formats.map((f) => [Number(f.id), f]));
  const gallonCosts = [];

  for (const row of costs) {
    if (Number(row.paint_product_id) !== Number(productId)) continue;
    if (String(row.cost_mode || '').toLowerCase() === 'auto') continue;
    const cost = Number(row.product_cost);
    if (Number.isNaN(cost) || cost < 0) continue;

    const fmt = formatById.get(Number(row.format_id));
    const formatLabel = fmt?.display_name || row.format_name || '';
    const liters = parseFormatVolumeLiters(formatLabel);
    if (!liters || liters <= 0) continue;

    if (fmt?.category_code && fmt.category_code !== 'V') continue;

    const gallonUnits = liters / GALLON_VOLUME_LITERS;
    gallonCosts.push(cost / gallonUnits);
  }

  if (!gallonCosts.length) return null;
  return { min: Math.min(...gallonCosts), max: Math.max(...gallonCosts) };
};

const formatGallonCostLabel = (range) => {
  if (!range) return null;
  if (range.min === range.max) return `${formatCostCl(range.min)} / gal`;
  return `${formatCostCl(range.min)} – ${formatCostCl(range.max)} / gal`;
};

const createEmptyCostLine = () => ({
  key: `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  formatId: '',
  colorId: '',
  costMode: 'manual',
  productCost: '',
});

const formatIdsFromCostLines = (lines) =>
  [...new Set(lines.map((line) => Number(line.formatId)).filter((id) => id > 0))].sort(
    (a, b) => a - b
  );

const validateCostLines = (lines) => {
  const seen = new Set();
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const formatId = Number(line.formatId);
    const colorId = Number(line.colorId);
    if (!formatId || !colorId) {
      return `Línea ${i + 1}: selecciona formato y color.`;
    }
    const pair = `${formatId}-${colorId}`;
    if (seen.has(pair)) {
      return `Línea ${i + 1}: combinación formato/color duplicada.`;
    }
    seen.add(pair);
    if (line.costMode === 'manual') {
      const amount = Number(line.productCost);
      if (Number.isNaN(amount) || amount < 0) {
        return `Línea ${i + 1}: indica un costo manual válido.`;
      }
    }
  }
  return null;
};

async function persistProductCosts(productId, lines, removedIds = []) {
  for (const costId of removedIds) {
    const res = await authFetch(`${API_BASE_URL}/api/quotations/products/costs/${costId}`, {
      method: 'DELETE',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(formatApiError(data.detail) || `HTTP ${res.status}`);
  }
  for (const line of lines) {
    const payload = {
      format_id: Number(line.formatId),
      color_id: Number(line.colorId),
      cost_mode: line.costMode,
      product_cost: line.costMode === 'manual' ? Number(line.productCost) : null,
    };
    if (line.id) {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/costs/${line.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data.detail) || `HTTP ${res.status}`);
    } else {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/costs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paint_product_id: productId, ...payload }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data.detail) || `HTTP ${res.status}`);
    }
  }
}

function ProductCostLinesTable({
  lines,
  onAddLine,
  onUpdateLine,
  onRemoveLine,
  formats,
  colors,
  productSku,
  disabled,
  border,
  muted,
  text,
  inputBg,
  selectStyle,
}) {
  const activeFormats = formats.filter((f) => Number(f.is_active) === 1);
  const activeColors = colors.filter((c) => String(c.status || 'Activo') === 'Activo');
  const gridColumns = 'minmax(0, 1.25fr) minmax(0, 1.25fr) minmax(0, 1fr) minmax(0, 0.8fr) minmax(0, 0.8fr) 36px';
  const lineSelectStyle = {
    ...selectStyle,
    minWidth: 0,
    width: '100%',
    fontFamily: 'inherit',
    textAlign: 'center',
  };
  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    boxSizing: 'border-box',
    padding: '8px 6px',
    fontSize: '10px',
    fontWeight: 700,
    color: muted,
    textTransform: 'uppercase',
    textAlign: 'center',
    background: inputBg,
    borderBottom: `1px solid ${border}`,
  };
  const cellStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    boxSizing: 'border-box',
    padding: '6px',
    minWidth: 0,
  };
  const rowBorderStyle = (isLastRow) =>
    isLastRow ? undefined : { borderBottom: `1px solid ${border}` };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: gridColumns,
          border: `1px solid ${border}`,
          borderRadius: '8px',
          overflow: 'hidden',
          alignItems: 'center',
          justifyItems: 'stretch',
        }}
      >
        {['Formato', 'Color', 'SKU', 'Modo', 'Costo', ''].map((label) => (
          <div key={label || 'actions'} style={headerStyle}>
            {label}
          </div>
        ))}
        {lines.length === 0 ? (
          <div
            style={{
              gridColumn: '1 / -1',
              padding: '16px',
              textAlign: 'center',
              color: muted,
              fontSize: '12px',
            }}
          >
            Sin líneas de costo. Usa «Agregar línea» para definir formatos y costos.
          </div>
        ) : (
          lines.map((line, lineIndex) => {
            const format = activeFormats.find((f) => Number(f.id) === Number(line.formatId));
            const color = activeColors.find((c) => Number(c.id) === Number(line.colorId));
            const previewSku = buildPreviewFullSku(productSku, format?.format_code, color?.color_code);
            const isLastRow = lineIndex === lines.length - 1;
            const rowBorder = rowBorderStyle(isLastRow);
            return (
              <React.Fragment key={line.key}>
                <div style={{ ...cellStyle, ...rowBorder }}>
                  <select
                    value={line.formatId}
                    onChange={(e) => onUpdateLine(line.key, 'formatId', e.target.value)}
                    disabled={disabled}
                    style={lineSelectStyle}
                  >
                    <option value="">Formato…</option>
                    {activeFormats.map((f) => (
                      <option key={f.id} value={String(f.id)}>
                        {`${f.display_name} (${f.format_code})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ ...cellStyle, ...rowBorder }}>
                  <select
                    value={line.colorId}
                    onChange={(e) => onUpdateLine(line.key, 'colorId', e.target.value)}
                    disabled={disabled}
                    style={lineSelectStyle}
                  >
                    <option value="">Color…</option>
                    {activeColors.map((c) => (
                      <option key={c.id} value={String(c.id)}>
                        {`${c.color_name} (${c.color_code})`}
                      </option>
                    ))}
                  </select>
                </div>
                <div
                  style={{
                    ...cellStyle,
                    ...rowBorder,
                    fontSize: '13px',
                    fontWeight: 600,
                    color: text,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {previewSku || '—'}
                </div>
                <div style={{ ...cellStyle, ...rowBorder }}>
                  <select
                    value={line.costMode}
                    onChange={(e) => onUpdateLine(line.key, 'costMode', e.target.value)}
                    disabled={disabled}
                    style={{ ...lineSelectStyle, width: '100%', maxWidth: '110px' }}
                  >
                    <option value="manual">Manual</option>
                    <option value="auto">Automático</option>
                  </select>
                </div>
                <div style={{ ...cellStyle, ...rowBorder }}>
                  <input
                    type="number"
                    min={0}
                    step={100}
                    value={line.productCost}
                    onChange={(e) => onUpdateLine(line.key, 'productCost', e.target.value)}
                    disabled={disabled || line.costMode === 'auto'}
                    placeholder={line.costMode === 'auto' ? 'Auto' : '0'}
                    style={{ ...lineSelectStyle, width: '100%', maxWidth: '96px' }}
                  />
                </div>
                <div style={{ ...cellStyle, ...rowBorder }}>
                  <button
                    type="button"
                    onClick={() => onRemoveLine(line.key)}
                    disabled={disabled}
                    title="Quitar línea"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '6px',
                      borderRadius: '6px',
                      border: '1px solid #fecaca',
                      background: '#fef2f2',
                      color: '#b91c1c',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <Trash2 size={14} aria-hidden />
                  </button>
                </div>
              </React.Fragment>
            );
          })
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <button
          type="button"
          onClick={onAddLine}
          disabled={disabled}
          style={{
            alignSelf: 'flex-start',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '6px 12px',
            borderRadius: '8px',
            border: `1px solid ${border}`,
            background: inputBg,
            color: text,
            fontSize: '12px',
            fontWeight: 600,
            cursor: disabled ? 'not-allowed' : 'pointer',
          }}
        >
          <Plus size={14} aria-hidden />
          Agregar línea
        </button>
        <span style={{ fontSize: '11px', color: muted }}>
          Los formatos del producto se derivan de las líneas. El modo automático se calculará cuando existan
          formulaciones y materias primas.
        </span>
      </div>
    </div>
  );
}

const PAINT_TABLE_COLUMNS = [
  { key: 'SKU', align: 'left', sortKey: 'sku' },
  { key: 'Producto', align: 'left', sortKey: 'product_name' },
  { key: 'Formatos', align: 'left', sortKey: 'formats' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
  { key: 'Actualizado por', align: 'left', sortKey: 'updated_by' },
];

const COLOR_TABLE_COLUMNS = [
  { key: 'Código', align: 'left', sortKey: 'color_code' },
  { key: 'Fam.', align: 'right', sortKey: 'family_digit' },
  { key: 'Familia RAL', align: 'left', sortKey: 'family_name' },
  { key: 'Color', align: 'left', sortKey: 'color_name' },
  { key: 'Hex', align: 'left', sortKey: 'hex_value' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
];

const RAL_FAMILY_OPTIONS = [
  { digit: 0, name: 'Bases' },
  { digit: 1, name: 'Amarillos' },
  { digit: 2, name: 'Naranjas' },
  { digit: 3, name: 'Rojos' },
  { digit: 4, name: 'Violetas' },
  { digit: 5, name: 'Azules' },
  { digit: 6, name: 'Verdes' },
  { digit: 7, name: 'Grises' },
  { digit: 8, name: 'Marrones' },
  { digit: 9, name: 'Blancos y Negros' },
];

const getPaintSortValue = (row, sortKey, formats = []) => {
  switch (sortKey) {
    case 'sku':
      return String(row.sku || '').toLowerCase();
    case 'product_name':
      return String(row.product_name || '').toLowerCase();
    case 'formats':
      return getProductFormatLabel(row, formats).toLowerCase();
    case 'updated_by':
      return String(row.updated_by || '').toLowerCase();
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const comparePaintRows = (a, b, sortKey, sortDir, formats = []) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getPaintSortValue(a, sortKey, formats);
  const vb = getPaintSortValue(b, sortKey, formats);
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

const getColorSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'color_code':
      return String(row.color_code || '').toLowerCase();
    case 'family_digit':
      return Number(row.family_digit) || 0;
    case 'family_name':
      return String(row.family_name || '').toLowerCase();
    case 'color_name':
      return String(row.color_name || '').toLowerCase();
    case 'hex_value':
      return String(row.hex_value || '').toLowerCase();
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareColorRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getColorSortValue(a, sortKey);
  const vb = getColorSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/** Imagen del producto en tarjeta; si falla la URL o no hay, muestra placeholder. */
function PaintProductCardImage({ imageUrl, sku, border, surfaceBg, iconColor }) {
  const [failed, setFailed] = useState(false);
  const trimmed = String(imageUrl || '').trim();
  const showImg = Boolean(trimmed) && !failed;

  return (
    <div
      style={{
        width: '100%',
        aspectRatio: '4 / 3',
        background: surfaceBg,
        borderBottom: `1px solid ${border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {showImg ? (
        <img
          src={trimmed}
          alt=""
          onError={() => setFailed(true)}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            padding: '10px',
            boxSizing: 'border-box',
          }}
        />
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px 12px',
            color: iconColor,
            textAlign: 'center',
            gap: '8px',
          }}
        >
          <Paintbrush size={36} strokeWidth={1.35} aria-hidden />
          <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', opacity: 0.9 }}>
            {sku || 'T-COAT'}
          </span>
        </div>
      )}
    </div>
  );
}

/**
 * T-COAT: pestañas Productos / Colores / Formatos con el mismo look que UpsellingTable3.
 */
const PaintProductsTable3 = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  void isDarkMode;
  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;

  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;

  const [rows, setRows] = useState([]);
  const [productCosts, setProductCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortColumn, setSortColumn] = useState('sku');
  const [sortDir, setSortDir] = useState('asc');
  /** Vista de la pestaña Productos: tabla clásica o cuadrícula de tarjetas. */
  const [productsView, setProductsView] = useState('table');

  const [colors, setColors] = useState([]);
  const [colorsLoading, setColorsLoading] = useState(true);
  const [colorsError, setColorsError] = useState('');
  const [colorSearchQuery, setColorSearchQuery] = useState('');
  const [colorSortColumn, setColorSortColumn] = useState('color_code');
  const [colorSortDir, setColorSortDir] = useState('asc');

  const [workspaceTab, setWorkspaceTab] = useState('products');
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedColorId, setSelectedColorId] = useState(null);
  const [selectedFormatId, setSelectedFormatId] = useState(null);

  const [formats, setFormats] = useState([]);
  const [formatsLoading, setFormatsLoading] = useState(false);
  const [formatCategories, setFormatCategories] = useState([]);
  const [formatCategoriesLoading, setFormatCategoriesLoading] = useState(false);
  const [showFormatModal, setShowFormatModal] = useState(false);
  const [newFormatCategoryId, setNewFormatCategoryId] = useState('');
  const [newFormatName, setNewFormatName] = useState('');
  const [saveFormatLoading, setSaveFormatLoading] = useState(false);
  const [formatFormError, setFormatFormError] = useState('');
  const [showEditFormatModal, setShowEditFormatModal] = useState(false);
  const [editFormatId, setEditFormatId] = useState(null);
  const [editFormatCode, setEditFormatCode] = useState('');
  const [editFormatName, setEditFormatName] = useState('');
  const [editFormatSortOrder, setEditFormatSortOrder] = useState('');
  const [editFormatIsActive, setEditFormatIsActive] = useState(true);
  const [editFormatHasCategory, setEditFormatHasCategory] = useState(false);
  const [editFormatCategoryLabel, setEditFormatCategoryLabel] = useState('');
  const [saveEditFormatLoading, setSaveEditFormatLoading] = useState(false);
  const [editFormatFormError, setEditFormatFormError] = useState('');
  const [showFormatCategoryModal, setShowFormatCategoryModal] = useState(false);
  const [newFormatCategoryCode, setNewFormatCategoryCode] = useState('');
  const [newFormatCategoryName, setNewFormatCategoryName] = useState('');
  const [saveFormatCategoryLoading, setSaveFormatCategoryLoading] = useState(false);
  const [formatCategoryFormError, setFormatCategoryFormError] = useState('');
  const [showEditFormatCategoryModal, setShowEditFormatCategoryModal] = useState(false);
  const [editFormatCategoryId, setEditFormatCategoryId] = useState(null);
  const [editFormatCategoryCode, setEditFormatCategoryCode] = useState('');
  const [editFormatCategoryName, setEditFormatCategoryName] = useState('');
  const [editFormatCategoryIsActive, setEditFormatCategoryIsActive] = useState(true);
  const [saveEditFormatCategoryLoading, setSaveEditFormatCategoryLoading] = useState(false);
  const [editFormatCategoryFormError, setEditFormatCategoryFormError] = useState('');

  const [showProductModal, setShowProductModal] = useState(false);
  const [newProductSku, setNewProductSku] = useState('');
  const [newProductName, setNewProductName] = useState('');
  const [newProductStatus, setNewProductStatus] = useState('Activo');
  const [newProductCostLines, setNewProductCostLines] = useState([]);
  const [newProductImageFile, setNewProductImageFile] = useState(null);
  const [newProductImagePreview, setNewProductImagePreview] = useState('');
  const [saveProductLoading, setSaveProductLoading] = useState(false);
  const [productFormError, setProductFormError] = useState('');

  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editProductId, setEditProductId] = useState(null);
  const [editProductSku, setEditProductSku] = useState('');
  const [editProductName, setEditProductName] = useState('');
  const [editProductStatus, setEditProductStatus] = useState('Activo');
  const [editProductCostLines, setEditProductCostLines] = useState([]);
  const [editProductRemovedCostIds, setEditProductRemovedCostIds] = useState([]);
  const [editProductExistingImagePath, setEditProductExistingImagePath] = useState('');
  const [editProductImageFile, setEditProductImageFile] = useState(null);
  const [editProductImagePreview, setEditProductImagePreview] = useState('');
  const [editProductRemoveImage, setEditProductRemoveImage] = useState(false);
  const [saveEditProductLoading, setSaveEditProductLoading] = useState(false);
  const [editProductFormError, setEditProductFormError] = useState('');

  const [showColorModal, setShowColorModal] = useState(false);
  const [newColorCode, setNewColorCode] = useState('');
  const [newColorName, setNewColorName] = useState('');
  const [newColorFamilyDigit, setNewColorFamilyDigit] = useState('9');
  const [newColorHex, setNewColorHex] = useState('');
  const [newColorIsRal, setNewColorIsRal] = useState(false);
  const [newColorStatus, setNewColorStatus] = useState('Activo');
  const [saveColorLoading, setSaveColorLoading] = useState(false);
  const [colorFormError, setColorFormError] = useState('');
  const [showEditColorModal, setShowEditColorModal] = useState(false);
  const [editColorId, setEditColorId] = useState(null);
  const [editColorCode, setEditColorCode] = useState('');
  const [editColorName, setEditColorName] = useState('');
  const [editColorFamilyDigit, setEditColorFamilyDigit] = useState('9');
  const [editColorHex, setEditColorHex] = useState('');
  const [editColorIsRal, setEditColorIsRal] = useState(false);
  const [editColorIsActive, setEditColorIsActive] = useState(true);
  const [saveEditColorLoading, setSaveEditColorLoading] = useState(false);
  const [editColorFormError, setEditColorFormError] = useState('');

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '160px',
    outline: 'none',
  };

  const clearProductImageSelection = useCallback(() => {
    setNewProductImageFile(null);
    setNewProductImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, []);

  useEffect(() => {
    return () => {
      if (newProductImagePreview) URL.revokeObjectURL(newProductImagePreview);
    };
  }, [newProductImagePreview]);

  const clearEditProductImageSelection = useCallback(() => {
    setEditProductImageFile(null);
    setEditProductImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
  }, []);

  useEffect(() => {
    return () => {
      if (editProductImagePreview) URL.revokeObjectURL(editProductImagePreview);
    };
  }, [editProductImagePreview]);

  const handleProductImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      clearProductImageSelection();
      return;
    }
    if (!ALLOWED_PRODUCT_IMAGE_TYPES.includes(file.type)) {
      setProductFormError('Formato no permitido. Usa JPEG, PNG o WebP.');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_PRODUCT_IMAGE_BYTES) {
      setProductFormError('La imagen no puede superar 2 MB.');
      event.target.value = '';
      return;
    }
    setProductFormError('');
    setNewProductImageFile(file);
    setNewProductImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleEditProductImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      clearEditProductImageSelection();
      return;
    }
    if (!ALLOWED_PRODUCT_IMAGE_TYPES.includes(file.type)) {
      setEditProductFormError('Formato no permitido. Usa JPEG, PNG o WebP.');
      event.target.value = '';
      return;
    }
    if (file.size > MAX_PRODUCT_IMAGE_BYTES) {
      setEditProductFormError('La imagen no puede superar 2 MB.');
      event.target.value = '';
      return;
    }
    setEditProductFormError('');
    setEditProductRemoveImage(false);
    setEditProductImageFile(file);
    setEditProductImagePreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setRows([]);
      setError('No se pudieron cargar los productos de pintura T-COAT.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadColors = useCallback(async () => {
    setColorsLoading(true);
    setColorsError('');
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/colors`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setColors(
        list.map((row) => ({
          ...row,
          color_name: normalizeColorDisplayName(row.color_name),
        }))
      );
    } catch (e) {
      console.error(e);
      setColors([]);
      setColorsError('No se pudieron cargar los colores.');
    } finally {
      setColorsLoading(false);
    }
  }, []);

  const loadFormats = useCallback(async () => {
    setFormatsLoading(true);
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/quotations/products/formats?include_inactive=true`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFormats(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setFormats([]);
    } finally {
      setFormatsLoading(false);
    }
  }, []);

  const loadProductCosts = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/costs`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProductCosts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setProductCosts([]);
    }
  }, []);

  const loadFormatCategories = useCallback(async () => {
    setFormatCategoriesLoading(true);
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/quotations/products/formats/categories?include_inactive=true`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFormatCategories(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setFormatCategories([]);
    } finally {
      setFormatCategoriesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
    loadColors();
    loadFormats();
    loadFormatCategories();
    loadProductCosts();
  }, [loadProducts, loadColors, loadFormats, loadFormatCategories, loadProductCosts]);

  const filteredRows = useMemo(() => {
    let list = rows;
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.sku,
          row.product_name,
          getProductFormatLabel(row, formats),
          row.status,
          row.updated_by,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    return list;
  }, [rows, searchQuery, formats]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => comparePaintRows(a, b, sortColumn, sortDir, formats));
    return arr;
  }, [filteredRows, sortColumn, sortDir, formats]);

  const filteredColors = useMemo(() => {
    let list = colors;
    const q = colorSearchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.color_code,
          row.family_digit,
          row.family_name,
          row.color_name,
          row.hex_value,
          row.status,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    return list;
  }, [colors, colorSearchQuery]);

  const sortedColors = useMemo(() => {
    const arr = [...filteredColors];
    arr.sort((a, b) => compareColorRows(a, b, colorSortColumn, colorSortDir));
    return arr;
  }, [filteredColors, colorSortColumn, colorSortDir]);

  const formatsRowsOrdered = useMemo(() => {
    const arr = [...formats];
    arr.sort((a, b) => {
      const o = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
      if (o !== 0) return o;
      return String(a.display_name || '').localeCompare(String(b.display_name || ''), 'es');
    });
    return arr;
  }, [formats]);

  const selectedProduct = useMemo(
    () => rows.find((row) => Number(row.id) === Number(selectedProductId)) || null,
    [rows, selectedProductId]
  );
  const selectedColor = useMemo(
    () => colors.find((row) => Number(row.id) === Number(selectedColorId)) || null,
    [colors, selectedColorId]
  );
  const selectedFormat = useMemo(
    () => formats.find((row) => Number(row.id) === Number(selectedFormatId)) || null,
    [formats, selectedFormatId]
  );

  useEffect(() => {
    if (selectedProductId && !rows.some((row) => Number(row.id) === Number(selectedProductId))) {
      setSelectedProductId(null);
    }
  }, [rows, selectedProductId]);

  useEffect(() => {
    if (selectedColorId && !colors.some((row) => Number(row.id) === Number(selectedColorId))) {
      setSelectedColorId(null);
    }
  }, [colors, selectedColorId]);

  useEffect(() => {
    if (selectedFormatId && !formats.some((row) => Number(row.id) === Number(selectedFormatId))) {
      setSelectedFormatId(null);
    }
  }, [formats, selectedFormatId]);

  const formatCategoriesOrdered = useMemo(() => {
    const arr = [...formatCategories];
    arr.sort((a, b) => {
      const o = (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
      if (o !== 0) return o;
      return String(a.display_name || '').localeCompare(String(b.display_name || ''), 'es');
    });
    return arr;
  }, [formatCategories]);

  const activeFormatCategories = useMemo(
    () => formatCategoriesOrdered.filter((c) => Number(c.is_active) === 1),
    [formatCategoriesOrdered]
  );

  const newFormatCodePreview = useMemo(() => {
    const categoryId = Number(newFormatCategoryId);
    if (!categoryId) return '';
    const category = formatCategories.find((c) => Number(c.id) === categoryId);
    if (!category) return '';
    const inCategory = formats.filter((f) => Number(f.category_id) === categoryId);
    return buildNextFormatCodePreview(category.category_code, inCategory);
  }, [newFormatCategoryId, formatCategories, formats]);

  const gallonCostByProductId = useMemo(() => {
    const map = new Map();
    const productIds = [...new Set(productCosts.map((c) => Number(c.paint_product_id)).filter((id) => id > 0))];
    for (const productId of productIds) {
      const range = computeGallonCostRange(productId, productCosts, formats);
      if (range) map.set(productId, range);
    }
    return map;
  }, [productCosts, formats]);

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const handleColorSortClick = (key) => {
    if (colorSortColumn === key) {
      setColorSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setColorSortColumn(key);
      setColorSortDir('asc');
    }
  };

  const clearFilters = () => setSearchQuery('');
  const clearColorFilters = () => setColorSearchQuery('');

  const openFormatModal = () => {
    setFormatFormError('');
    const firstActive = activeFormatCategories[0];
    setNewFormatCategoryId(firstActive ? String(firstActive.id) : '');
    setNewFormatName('');
    setShowFormatModal(true);
  };

  const openFormatCategoryModal = () => {
    setFormatCategoryFormError('');
    setNewFormatCategoryCode('');
    setNewFormatCategoryName('');
    setShowFormatCategoryModal(true);
  };

  const openEditFormatCategory = (category) => {
    if (category.id == null) return;
    setEditFormatCategoryFormError('');
    setEditFormatCategoryId(category.id);
    setEditFormatCategoryCode(String(category.category_code || ''));
    setEditFormatCategoryName(String(category.display_name || ''));
    setEditFormatCategoryIsActive(Number(category.is_active) === 1);
    setShowEditFormatCategoryModal(true);
  };

  const openProductModal = () => {
    setProductFormError('');
    setNewProductSku('');
    setNewProductName('');
    setNewProductStatus('Activo');
    setNewProductCostLines([]);
    clearProductImageSelection();
    setShowProductModal(true);
  };

  const addNewProductCostLine = () => {
    setNewProductCostLines((prev) => [...prev, createEmptyCostLine()]);
  };

  const updateNewProductCostLine = (key, field, value) => {
    setNewProductCostLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, [field]: value } : line))
    );
  };

  const removeNewProductCostLine = (key) => {
    setNewProductCostLines((prev) => prev.filter((line) => line.key !== key));
  };

  const addEditProductCostLine = () => {
    setEditProductCostLines((prev) => [...prev, createEmptyCostLine()]);
  };

  const updateEditProductCostLine = (key, field, value) => {
    setEditProductCostLines((prev) =>
      prev.map((line) => (line.key === key ? { ...line, [field]: value } : line))
    );
  };

  const removeEditProductCostLine = (key) => {
    setEditProductCostLines((prev) => {
      const target = prev.find((line) => line.key === key);
      if (target?.id) {
        setEditProductRemovedCostIds((ids) => [...ids, target.id]);
      }
      return prev.filter((line) => line.key !== key);
    });
  };

  const openEditProduct = async (row) => {
    if (row.id == null) return;
    setEditProductFormError('');
    setEditProductId(row.id);
    setEditProductSku(String(row.sku || ''));
    setEditProductName(String(row.product_name || ''));
    setEditProductStatus(String(row.status || 'Activo'));
    setEditProductCostLines([]);
    setEditProductRemovedCostIds([]);
    setEditProductExistingImagePath(String(row.image_path || ''));
    setEditProductRemoveImage(false);
    clearEditProductImageSelection();
    setShowEditProductModal(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/costs?product_id=${row.id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEditProductCostLines(
        (Array.isArray(data) ? data : []).map((c) => ({
          key: `c-${c.id}`,
          id: c.id,
          formatId: String(c.format_id),
          colorId: String(c.color_id),
          costMode: c.cost_mode === 'auto' ? 'auto' : 'manual',
          productCost:
            c.product_cost !== null && c.product_cost !== undefined ? String(c.product_cost) : '',
        }))
      );
    } catch (e) {
      console.error(e);
      setEditProductFormError('No se pudieron cargar las líneas de costo del producto.');
    }
  };

  const handleSaveProduct = async () => {
    setProductFormError('');
    const sku = String(newProductSku || '').trim().toUpperCase();
    const productName = String(newProductName || '').trim();
    if (sku.length < 2) {
      setProductFormError('El SKU debe tener al menos 2 caracteres.');
      return;
    }
    if (!productName) {
      setProductFormError('Indica el nombre del producto.');
      return;
    }
    const lineError = validateCostLines(newProductCostLines);
    if (lineError) {
      setProductFormError(lineError);
      return;
    }
    const formatIds = formatIdsFromCostLines(newProductCostLines);
    setSaveProductLoading(true);
    try {
      const formData = new FormData();
      formData.append('sku', sku);
      formData.append('product_name', productName);
      formData.append('status', newProductStatus);
      formData.append('available_format_ids', JSON.stringify(formatIds));
      if (newProductImageFile) {
        formData.append('image', newProductImageFile);
      }
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products`, {
        method: 'POST',
        body: formData,
      });
      const created = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(created.detail) || `HTTP ${res.status}`);
      }
      const productId = created?.id;
      if (productId && newProductCostLines.length) {
        await persistProductCosts(productId, newProductCostLines);
      }
      setShowProductModal(false);
      clearProductImageSelection();
      setNewProductCostLines([]);
      await loadProducts();
      await loadProductCosts();
    } catch (e) {
      setProductFormError(e?.message || 'No se pudo crear el producto.');
    } finally {
      setSaveProductLoading(false);
    }
  };

  const handleSaveEditProduct = async () => {
    if (editProductId == null) return;
    setEditProductFormError('');
    const sku = String(editProductSku || '').trim().toUpperCase();
    const productName = String(editProductName || '').trim();
    if (sku.length < 2) {
      setEditProductFormError('El SKU debe tener al menos 2 caracteres.');
      return;
    }
    if (!productName) {
      setEditProductFormError('Indica el nombre del producto.');
      return;
    }
    const lineError = validateCostLines(editProductCostLines);
    if (lineError) {
      setEditProductFormError(lineError);
      return;
    }
    const formatIds = formatIdsFromCostLines(editProductCostLines);
    setSaveEditProductLoading(true);
    try {
      const formData = new FormData();
      formData.append('sku', sku);
      formData.append('product_name', productName);
      formData.append('status', editProductStatus);
      formData.append('available_format_ids', JSON.stringify(formatIds));
      formData.append('remove_image', editProductRemoveImage ? 'true' : 'false');
      if (editProductImageFile) {
        formData.append('image', editProductImageFile);
      }
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/${editProductId}`, {
        method: 'PUT',
        body: formData,
      });
      const updated = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(updated.detail) || `HTTP ${res.status}`);
      }
      await persistProductCosts(editProductId, editProductCostLines, editProductRemovedCostIds);
      setShowEditProductModal(false);
      setSelectedProductId(null);
      clearEditProductImageSelection();
      setEditProductCostLines([]);
      setEditProductRemovedCostIds([]);
      await loadProducts();
      await loadProductCosts();
    } catch (e) {
      setEditProductFormError(e?.message || 'No se pudo guardar el producto.');
    } finally {
      setSaveEditProductLoading(false);
    }
  };

  const handleChangeProductStatus = async (row) => {
    if (!row?.id) return;
    const currentStatus = String(row.status || 'Activo').trim() || 'Activo';
    const nextStatus = currentStatus === 'Activo' ? 'Inactivo' : 'Activo';
    const ok = window.confirm(
      nextStatus === 'Inactivo'
        ? `¿Desactivar el producto «${row.sku} — ${row.product_name}»?`
        : `¿Activar el producto «${row.sku} — ${row.product_name}»?`
    );
    if (!ok) return;
    try {
      const formData = new FormData();
      formData.append('sku', String(row.sku || '').trim().toUpperCase());
      formData.append('product_name', String(row.product_name || '').trim());
      formData.append('status', nextStatus);
      formData.append('available_format_ids', JSON.stringify(row.available_format_ids || []));
      formData.append('remove_image', 'false');

      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/${row.id}`, {
        method: 'PUT',
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data.detail) || `HTTP ${res.status}`);
      await loadProducts();
    } catch (e) {
      window.alert(e?.message || 'No se pudo cambiar el estado del producto.');
    }
  };

  const handleDeleteProduct = async (row) => {
    if (!row?.id) return;
    const ok = window.confirm(
      `¿Eliminar el producto «${row.sku} — ${row.product_name}» de la tabla? Esta acción lo quitará del listado.`
    );
    if (!ok) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/${row.id}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(data.detail) || `HTTP ${res.status}`);
      setSelectedProductId(null);
      await loadProducts();
      await loadProductCosts();
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar el producto.');
    }
  };

  const handleSaveFormat = async () => {
    setFormatFormError('');
    const categoryId = Number(newFormatCategoryId);
    const displayName = String(newFormatName || '').trim();
    if (!categoryId) {
      setFormatFormError('Selecciona una categoría.');
      return;
    }
    if (!displayName) {
      setFormatFormError('Indica el nombre del formato.');
      return;
    }
    if (!newFormatCodePreview) {
      setFormatFormError('Esta categoría ya tiene el máximo de formatos (26).');
      return;
    }
    setSaveFormatLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/formats`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_id: categoryId, display_name: displayName }),
      });
      const errData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(errData.detail) || `HTTP ${res.status}`);
      }
      setShowFormatModal(false);
      await loadFormats();
    } catch (e) {
      setFormatFormError(e?.message || 'No se pudo crear el formato.');
    } finally {
      setSaveFormatLoading(false);
    }
  };

  const handleSaveFormatCategory = async () => {
    setFormatCategoryFormError('');
    const code = String(newFormatCategoryCode || '').trim().toUpperCase();
    const name = String(newFormatCategoryName || '').trim();
    if (code.length !== 1 || !/^[A-Z]$/.test(code)) {
      setFormatCategoryFormError('El código debe ser una sola letra (A-Z).');
      return;
    }
    if (!name) {
      setFormatCategoryFormError('Indica el nombre de la categoría.');
      return;
    }
    setSaveFormatCategoryLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/formats/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_code: code, display_name: name }),
      });
      const errData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(errData.detail) || `HTTP ${res.status}`);
      }
      setShowFormatCategoryModal(false);
      await loadFormatCategories();
    } catch (e) {
      setFormatCategoryFormError(e?.message || 'No se pudo crear la categoría.');
    } finally {
      setSaveFormatCategoryLoading(false);
    }
  };

  const handleSaveEditFormatCategory = async () => {
    if (editFormatCategoryId == null) return;
    setEditFormatCategoryFormError('');
    const name = String(editFormatCategoryName || '').trim();
    if (!name) {
      setEditFormatCategoryFormError('Indica el nombre de la categoría.');
      return;
    }
    setSaveEditFormatCategoryLoading(true);
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/quotations/products/formats/categories/${editFormatCategoryId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            display_name: name,
            is_active: editFormatCategoryIsActive ? 1 : 0,
          }),
        }
      );
      const errData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(errData.detail) || `HTTP ${res.status}`);
      }
      setShowEditFormatCategoryModal(false);
      await loadFormatCategories();
      await loadFormats();
    } catch (e) {
      setEditFormatCategoryFormError(e?.message || 'No se pudo guardar la categoría.');
    } finally {
      setSaveEditFormatCategoryLoading(false);
    }
  };

  const handleDeleteFormatCategory = async (category) => {
    if (category.id == null) return;
    const ok = window.confirm(
      `¿Eliminar la categoría «${category.category_code} — ${category.display_name}»?`
    );
    if (!ok) return;
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/quotations/products/formats/categories/${category.id}`,
        { method: 'DELETE' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(data.detail) || `HTTP ${res.status}`);
      }
      await loadFormatCategories();
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar la categoría.');
    }
  };

  const openEditFormat = (f) => {
    if (f.id == null) return;
    setEditFormatFormError('');
    setEditFormatId(f.id);
    setEditFormatCode(String(f.format_code || ''));
    setEditFormatName(String(f.display_name || ''));
    setEditFormatSortOrder(String(f.sort_order ?? 0));
    setEditFormatIsActive(Number(f.is_active) === 1);
    setEditFormatHasCategory(f.category_id != null);
    setEditFormatCategoryLabel(formatCategoryLabel(f));
    setShowEditFormatModal(true);
  };

  const handleSaveEditFormat = async () => {
    if (editFormatId == null) return;
    setEditFormatFormError('');
    const name = String(editFormatName || '').trim();
    if (!name) {
      setEditFormatFormError('Indica el nombre.');
      return;
    }
    const sortNum = parseInt(String(editFormatSortOrder || '0'), 10);
    if (Number.isNaN(sortNum)) {
      setEditFormatFormError('Orden inválido.');
      return;
    }
    const payload = {
      display_name: name,
      sort_order: sortNum,
      is_active: editFormatIsActive ? 1 : 0,
    };
    if (!editFormatHasCategory) {
      const codeUp = String(editFormatCode || '').trim().toUpperCase();
      if (codeUp.length < 2 || codeUp.length > 12) {
        setEditFormatFormError('El código debe tener entre 2 y 12 caracteres.');
        return;
      }
      payload.format_code = codeUp;
    }
    setSaveEditFormatLoading(true);
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/quotations/products/formats/${editFormatId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const errData = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatApiError(errData.detail) || `HTTP ${res.status}`);
      }
      setShowEditFormatModal(false);
      setSelectedFormatId(null);
      await loadFormats();
    } catch (e) {
      setEditFormatFormError(e?.message || 'No se pudo guardar.');
    } finally {
      setSaveEditFormatLoading(false);
    }
  };

  const handleDeleteFormat = async (f) => {
    if (f.id == null) return;
    const ok = window.confirm(
      `¿Eliminar el formato «${f.format_code} — ${f.display_name}»?`
    );
    if (!ok) return;
    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/quotations/products/formats/${f.id}`,
        { method: 'DELETE' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      setSelectedFormatId(null);
      await loadFormats();
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar.');
    }
  };

  const hasActiveFilters = Boolean(searchQuery.trim());
  const hasColorFilters = Boolean(colorSearchQuery.trim());

  const productsSubtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} productos · A006`
        : `${rows.length} productos · A006_paint_products`);

  const colorsSubtitle = colorsLoading
    ? 'Cargando…'
    : colorsError ||
      (hasColorFilters
        ? `Mostrando ${sortedColors.length} de ${colors.length} colores · A007`
        : `${colors.length} colores · A007_paint_colors`);

  const formatsSubtitle = formatsLoading || formatCategoriesLoading
    ? 'Cargando formatos…'
    : `${formats.length} formatos · ${formatCategories.length} categorías · A017`;

  const paintColCount = 6;
  const colorColCount = 7;

  const newColorBtnStyle = {
    height: '34px',
    padding: '0 14px',
    borderRadius: '8px',
    border: 'none',
    background: '#16a34a',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  };

  const contextualActionBtnStyle = {
    height: '34px',
    padding: '0 12px',
    borderRadius: '8px',
    border: `1px solid ${border}`,
    background: inputBg,
    color: text,
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  };

  const contextualDangerBtnStyle = {
    ...contextualActionBtnStyle,
    border: '1px solid #fecaca',
    background: isDarkMode ? '#450a0a' : '#fef2f2',
    color: '#b91c1c',
  };

  const openColorModal = () => {
    setColorFormError('');
    setNewColorCode('');
    setNewColorName('');
    setNewColorFamilyDigit('9');
    setNewColorHex('');
    setNewColorIsRal(false);
    setNewColorStatus('Activo');
    setShowColorModal(true);
  };

  const openEditColor = (row) => {
    if (row.id == null) return;
    setEditColorFormError('');
    setEditColorId(row.id);
    setEditColorCode(String(row.color_code || ''));
    setEditColorName(String(row.color_name || ''));
    setEditColorFamilyDigit(String(row.family_digit ?? 9));
    setEditColorHex(String(row.hex_value || ''));
    setEditColorIsRal(Boolean(row.is_ral));
    setEditColorIsActive(String(row.status || 'Activo') === 'Activo');
    setShowEditColorModal(true);
  };

  const handleSaveColor = async () => {
    setColorFormError('');
    const colorCode = String(newColorCode || '').trim().toUpperCase();
    const colorName = String(newColorName || '').trim();
    if (colorCode.length < 2) {
      setColorFormError('El código debe tener al menos 2 caracteres.');
      return;
    }
    if (!colorName) {
      setColorFormError('Indica el nombre del color.');
      return;
    }
    const familyDigit = parseInt(newColorFamilyDigit, 10);
    if (Number.isNaN(familyDigit) || familyDigit < 0 || familyDigit > 9) {
      setColorFormError('Selecciona una familia RAL válida.');
      return;
    }
    const hex = String(newColorHex || '').trim();
    if (hex && !/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      setColorFormError('Hex inválido. Usa formato #RRGGBB.');
      return;
    }
    const familyName = RAL_FAMILY_OPTIONS.find((f) => f.digit === familyDigit)?.name || 'Blancos y Negros';
    setSaveColorLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/colors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          color_code: colorCode,
          color_name: colorName,
          family_digit: familyDigit,
          family_name: familyName,
          hex_value: hex,
          is_ral: newColorIsRal,
          status: newColorStatus,
        }),
      });
      const errData = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errData.detail || `HTTP ${res.status}`);
      setShowColorModal(false);
      await loadColors();
    } catch (e) {
      setColorFormError(e?.message || 'No se pudo crear el color.');
    } finally {
      setSaveColorLoading(false);
    }
  };

  const handleSaveEditColor = async () => {
    if (editColorId == null) return;
    setEditColorFormError('');
    const colorName = String(editColorName || '').trim();
    if (!colorName) {
      setEditColorFormError('Indica el nombre del color.');
      return;
    }
    const familyDigit = parseInt(editColorFamilyDigit, 10);
    if (Number.isNaN(familyDigit) || familyDigit < 0 || familyDigit > 9) {
      setEditColorFormError('Familia RAL inválida.');
      return;
    }
    const hex = String(editColorHex || '').trim();
    if (hex && !/^#[0-9A-Fa-f]{6}$/.test(hex)) {
      setEditColorFormError('Hex inválido. Usa formato #RRGGBB.');
      return;
    }
    const codeUp = String(editColorCode || '').trim().toUpperCase();
    if (codeUp.length < 2) {
      setEditColorFormError('Código inválido.');
      return;
    }
    const familyName = RAL_FAMILY_OPTIONS.find((f) => f.digit === familyDigit)?.name || 'Blancos y Negros';
    setSaveEditColorLoading(true);
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/colors/${editColorId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          color_code: codeUp,
          color_name: colorName,
          family_digit: familyDigit,
          family_name: familyName,
          hex_value: hex,
          is_ral: editColorIsRal,
          status: editColorIsActive ? 'Activo' : 'Inactivo',
          is_active: editColorIsActive ? 1 : 0,
        }),
      });
      const errData = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errData.detail || `HTTP ${res.status}`);
      setShowEditColorModal(false);
      setSelectedColorId(null);
      await loadColors();
    } catch (e) {
      setEditColorFormError(e?.message || 'No se pudo guardar.');
    } finally {
      setSaveEditColorLoading(false);
    }
  };

  const handleDeleteColor = async (row) => {
    if (row.id == null) return;
    const ok = window.confirm(`¿Eliminar el color «${row.color_code} — ${row.color_name}»?`);
    if (!ok) return;
    try {
      const res = await authFetch(`${API_BASE_URL}/api/quotations/products/colors/${row.id}`, {
        method: 'DELETE',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setSelectedColorId(null);
      await loadColors();
    } catch (e) {
      window.alert(e?.message || 'No se pudo eliminar.');
    }
  };

  const renderHexCell = (hexValue) => {
    const hex = String(hexValue || '').trim();
    const isValidHex = /^#[0-9A-Fa-f]{6}$/.test(hex);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            border: `1px solid ${border}`,
            backgroundColor: isValidHex ? hex : muted,
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: '12px', color: text }}>{hex || '—'}</span>
      </span>
    );
  };

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ flex: '1 1 200px', display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>
            Pintura T-COAT
          </h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>
            {workspaceTab === 'products'
              ? productsSubtitle
              : workspaceTab === 'colors'
                ? colorsSubtitle
                : formatsSubtitle}
          </p>
        </div>
        <div
          style={{
            display: 'inline-flex',
            borderRadius: '8px',
            border: `1px solid ${border}`,
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={() => {
              setWorkspaceTab('products');
              setSelectedColorId(null);
              setSelectedFormatId(null);
            }}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: workspaceTab === 'products' ? ui.active : inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: workspaceTab === 'products' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Productos
          </button>
          <button
            type="button"
            onClick={() => {
              setWorkspaceTab('colors');
              setSelectedProductId(null);
              setSelectedFormatId(null);
            }}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderLeft: `1px solid ${border}`,
              background: workspaceTab === 'colors' ? ui.active : inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: workspaceTab === 'colors' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Colores
          </button>
          <button
            type="button"
            onClick={() => {
              setWorkspaceTab('formats');
              setSelectedProductId(null);
              setSelectedColorId(null);
            }}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderLeft: `1px solid ${border}`,
              background: workspaceTab === 'formats' ? ui.active : inputBg,
              color: text,
              fontSize: '12px',
              fontWeight: workspaceTab === 'formats' ? 700 : 500,
              cursor: 'pointer',
            }}
          >
            Formatos
          </button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {workspaceTab === 'products' ? (
            <>
              <button type="button" onClick={openProductModal} style={newColorBtnStyle}>
                <Plus size={15} aria-hidden />
                Nuevo producto
              </button>
              {selectedProduct ? (
                <>
                  <button type="button" onClick={() => openEditProduct(selectedProduct)} style={contextualActionBtnStyle}>
                    <Pencil size={15} aria-hidden />
                    Editar
                  </button>
                  <button
                    type="button"
                    onClick={() => handleChangeProductStatus(selectedProduct)}
                    style={contextualActionBtnStyle}
                    title={
                      String(selectedProduct.status || 'Activo') === 'Activo'
                        ? 'Desactivar producto'
                        : 'Activar producto'
                    }
                  >
                    <Power size={15} aria-hidden />
                    Cambiar Estado
                  </button>
                  <button type="button" onClick={() => handleDeleteProduct(selectedProduct)} style={contextualDangerBtnStyle}>
                    <Trash2 size={15} aria-hidden />
                    Eliminar
                  </button>
                </>
              ) : null}
            </>
          ) : null}
          {workspaceTab === 'colors' ? (
            <>
              <button type="button" onClick={openColorModal} style={newColorBtnStyle}>
                <Plus size={15} aria-hidden />
                Nuevo color
              </button>
              {selectedColor ? (
                <>
                  <button type="button" onClick={() => openEditColor(selectedColor)} style={contextualActionBtnStyle}>
                    <Pencil size={15} aria-hidden />
                    Editar
                  </button>
                  <button type="button" onClick={() => handleDeleteColor(selectedColor)} style={contextualDangerBtnStyle}>
                    <Trash2 size={15} aria-hidden />
                    Eliminar
                  </button>
                </>
              ) : null}
            </>
          ) : null}
          {workspaceTab === 'formats' ? (
            <>
              <button type="button" onClick={openFormatCategoryModal} style={newColorBtnStyle}>
                <Plus size={15} aria-hidden />
                Nueva categoría
              </button>
              <button type="button" onClick={openFormatModal} style={newColorBtnStyle}>
                <Plus size={15} aria-hidden />
                Nuevo formato
              </button>
              {selectedFormat ? (
                <>
                  <button type="button" onClick={() => openEditFormat(selectedFormat)} style={contextualActionBtnStyle}>
                    <Pencil size={15} aria-hidden />
                    Editar
                  </button>
                  <button type="button" onClick={() => handleDeleteFormat(selectedFormat)} style={contextualDangerBtnStyle}>
                    <Trash2 size={15} aria-hidden />
                    Eliminar
                  </button>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {workspaceTab === 'products' ? (
        <>
          <div
            style={{
              padding: '10px 16px',
              borderBottom: `1px solid ${border}`,
              background: bg,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              alignItems: 'flex-end',
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
                Buscar
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Search size={16} color={muted} aria-hidden />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="SKU, producto…"
                  style={{ ...selectStyle, flex: 1, minWidth: 0, height: '36px' }}
                />
              </span>
            </label>
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                style={{
                  height: '36px',
                  padding: '0 12px',
                  borderRadius: '8px',
                  border: `1px solid ${inputBorder}`,
                  background: inputBg,
                  color: muted,
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '2px',
                }}
              >
                Limpiar filtros
              </button>
            ) : null}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                marginBottom: '2px',
              }}
            >
              <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
                Vista
              </span>
              <div
                style={{
                  display: 'inline-flex',
                  borderRadius: '8px',
                  border: `1px solid ${border}`,
                  overflow: 'hidden',
                }}
              >
                <button
                  type="button"
                  aria-pressed={productsView === 'table'}
                  onClick={() => setProductsView('table')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '0 12px',
                    height: '36px',
                    border: 'none',
                    background:
                      productsView === 'table'
                        ? isDarkMode
                          ? '#27272a'
                          : '#e8e0d4'
                        : inputBg,
                    color: text,
                    fontSize: '12px',
                    fontWeight: productsView === 'table' ? 700 : 500,
                    cursor: 'pointer',
                  }}
                >
                  <LayoutList size={16} aria-hidden />
                  Tabla
                </button>
                <button
                  type="button"
                  aria-pressed={productsView === 'cards'}
                  onClick={() => setProductsView('cards')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '0 12px',
                    height: '36px',
                    border: 'none',
                    borderLeft: `1px solid ${border}`,
                    background:
                      productsView === 'cards'
                        ? isDarkMode
                          ? '#27272a'
                          : '#e8e0d4'
                        : inputBg,
                    color: text,
                    fontSize: '12px',
                    fontWeight: productsView === 'cards' ? 700 : 500,
                    cursor: 'pointer',
                  }}
                >
                  <LayoutGrid size={16} aria-hidden />
                  Tarjetas
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                loadProducts();
                loadColors();
                loadFormats();
                loadProductCosts();
              }}
              disabled={loading}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontSize: '12px',
                fontWeight: 600,
                cursor: loading ? 'wait' : 'pointer',
                marginBottom: '2px',
              }}
            >
              Actualizar
            </button>
          </div>

          {productsView === 'cards' ? (
            <div style={{ padding: '16px', background: bg }}>
              {loading ? (
                <div style={{ padding: '28px', textAlign: 'center', color: muted }}>Cargando productos…</div>
              ) : rows.length === 0 ? (
                <div style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  {error || 'No hay productos en A006.'}
                </div>
              ) : sortedRows.length === 0 ? (
                <div style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Ningún resultado con la búsqueda actual.
                </div>
              ) : (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: '16px',
                  }}
                >
                  {sortedRows.map((row) => {
                    const gallonCostLabel = formatGallonCostLabel(gallonCostByProductId.get(row.id));
                    const isSelected = Number(selectedProductId) === Number(row.id);
                    return (
                    <article
                      key={row.id}
                      onClick={() => setSelectedProductId((current) => (Number(current) === Number(row.id) ? null : row.id))}
                      style={{
                        border: `1px solid ${isSelected ? ui.interactive : border}`,
                        borderRadius: '12px',
                        overflow: 'hidden',
                        background: inputBg,
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'box-shadow 0.15s ease',
                        boxShadow: isSelected ? `0 0 0 2px ${ui.interactive}22` : ui.isDarkMode ? 'none' : ui.shadowSm,
                        cursor: 'pointer',
                      }}
                    >
                      <PaintProductCardImage
                        imageUrl={resolveProductImageUrl(row.image_path)}
                        sku={row.sku}
                        border={border}
                        surfaceBg={headerBg}
                        iconColor={muted}
                      />
                      <div style={{ padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div
                          style={{
                            fontSize: '11px',
                            fontWeight: 800,
                            color: '#2563eb',
                            letterSpacing: '0.02em',
                          }}
                        >
                          {row.sku || '—'}
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: text,
                            lineHeight: 1.35,
                            minHeight: '2.7em',
                          }}
                        >
                          {row.product_name || '—'}
                        </div>
                        {gallonCostLabel ? (
                          <div
                            style={{
                              fontSize: '12px',
                              fontWeight: 700,
                              color: isDarkMode ? '#86efac' : '#166534',
                              lineHeight: 1.3,
                            }}
                          >
                            {gallonCostLabel}
                          </div>
                        ) : null}
                        <div style={{ marginTop: '4px' }}>{renderStatusBadge(row.status)}</div>
                        {row.updated_by ? (
                          <div style={{ fontSize: '11px', color: muted }}>
                            Actualizado por {row.updated_by}
                          </div>
                        ) : null}
                      </div>
                    </article>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
              <thead>
                <tr style={{ background: headerBg }}>
                  <th
                    scope="col"
                    style={{
                      width: '42px',
                      padding: `${thPadV}px ${padH}px`,
                      borderBottom: `1px solid ${border}`,
                    }}
                  />
                  {PAINT_TABLE_COLUMNS.map(({ key: h, align, sortKey: sk }) => (
                    <th
                      key={h}
                      scope="col"
                      onClick={() => sk && handleSortClick(sk)}
                      onKeyDown={(e) => {
                        if (!sk) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleSortClick(sk);
                        }
                      }}
                      tabIndex={sk ? 0 : undefined}
                      style={{
                        textAlign: align,
                        padding: `${thPadV}px ${padH + 4}px`,
                        fontSize: '11px',
                        fontWeight: 700,
                        color: muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        borderBottom: `1px solid ${border}`,
                        cursor: sk ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent:
                            align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
                          gap: '6px',
                          width: align === 'center' || align === 'right' ? '100%' : 'auto',
                        }}
                      >
                        {h}
                        {sk ? (
                          sortColumn === sk ? (
                            sortDir === 'asc' ? (
                              <ArrowUp size={13} strokeWidth={2.5} aria-hidden />
                            ) : (
                              <ArrowDown size={13} strokeWidth={2.5} aria-hidden />
                            )
                          ) : (
                            <ArrowUpDown size={13} strokeWidth={2} style={{ opacity: 0.4 }} aria-hidden />
                          )
                        ) : null}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={paintColCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                      Cargando productos…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={paintColCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                      {error || 'No hay productos en A006.'}
                    </td>
                  </tr>
                ) : sortedRows.length === 0 ? (
                  <tr>
                    <td colSpan={paintColCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                      Ningún resultado con la búsqueda actual.
                    </td>
                  </tr>
                ) : (
                  sortedRows.map((row) => {
                    const isSelected = Number(selectedProductId) === Number(row.id);
                    return (
                    <tr
                      key={row.id}
                      style={{
                        borderBottom: `1px solid ${border}`,
                        transition: 'background 0.15s ease',
                        background: isSelected ? ui.active : 'transparent',
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectedProductId((current) => (Number(current) === Number(row.id) ? null : row.id))}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = rowHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isSelected ? ui.active : 'transparent';
                      }}
                    >
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => setSelectedProductId((current) => (Number(current) === Number(row.id) ? null : row.id))}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`Seleccionar ${row.sku || row.product_name || 'producto'}`}
                        />
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontWeight: 700, fontSize: '12px', color: text }}>
                        {row.sku || '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text, maxWidth: '280px' }}>
                        {row.product_name || '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: muted }}>
                        {getProductFormatLabel(row, formats)}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        {renderStatusBadge(row.status)}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: muted }}>
                        {row.updated_by || '—'}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          )}
        </>
      ) : workspaceTab === 'colors' ? (
        <div style={{ padding: '0 0 12px', background: bg }}>
          <div
            style={{
              padding: '10px 16px',
              borderBottom: `1px solid ${border}`,
              background: bg,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              alignItems: 'flex-end',
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
                Buscar
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Search size={16} color={muted} aria-hidden />
                <input
                  type="search"
                  value={colorSearchQuery}
                  onChange={(e) => setColorSearchQuery(e.target.value)}
                  placeholder="Código, familia, color…"
                  style={{ ...selectStyle, flex: 1, minWidth: 0, height: '36px' }}
                />
              </span>
            </label>
            {hasColorFilters ? (
              <button
                type="button"
                onClick={clearColorFilters}
                style={{
                  height: '36px',
                  padding: '0 12px',
                  borderRadius: '8px',
                  border: `1px solid ${inputBorder}`,
                  background: inputBg,
                  color: muted,
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  marginBottom: '2px',
                }}
              >
                Limpiar filtros
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                loadColors();
                loadProducts();
                loadFormats();
              }}
              disabled={colorsLoading}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontSize: '12px',
                fontWeight: 600,
                cursor: colorsLoading ? 'wait' : 'pointer',
                marginBottom: '2px',
              }}
            >
              Actualizar
            </button>
          </div>
          <div style={{ overflowX: 'auto', padding: '0 16px 12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '880px' }}>
              <thead>
                <tr style={{ background: headerBg }}>
                  <th
                    scope="col"
                    style={{
                      width: '42px',
                      padding: `${thPadV}px ${padH}px`,
                      borderBottom: `1px solid ${border}`,
                    }}
                  />
                  {COLOR_TABLE_COLUMNS.map(({ key: h, align, sortKey: sk }) => (
                    <th
                      key={h}
                      scope="col"
                      onClick={() => sk && handleColorSortClick(sk)}
                      onKeyDown={(e) => {
                        if (!sk) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleColorSortClick(sk);
                        }
                      }}
                      tabIndex={sk ? 0 : undefined}
                      style={{
                        textAlign: align,
                        padding: `${thPadV}px ${padH + 4}px`,
                        fontSize: '11px',
                        fontWeight: 700,
                        color: muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        borderBottom: `1px solid ${border}`,
                        cursor: sk ? 'pointer' : 'default',
                        userSelect: 'none',
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent:
                            align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start',
                          gap: '6px',
                          width: align === 'center' || align === 'right' ? '100%' : 'auto',
                        }}
                      >
                        {h}
                        {sk ? (
                          colorSortColumn === sk ? (
                            colorSortDir === 'asc' ? (
                              <ArrowUp size={13} strokeWidth={2.5} aria-hidden />
                            ) : (
                              <ArrowDown size={13} strokeWidth={2.5} aria-hidden />
                            )
                          ) : (
                            <ArrowUpDown size={13} strokeWidth={2} style={{ opacity: 0.4 }} aria-hidden />
                          )
                        ) : null}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {colorsLoading ? (
                  <tr>
                    <td colSpan={colorColCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                      Cargando colores…
                    </td>
                  </tr>
                ) : colors.length === 0 ? (
                  <tr>
                    <td colSpan={colorColCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                      {colorsError || 'No hay colores en A007.'}
                    </td>
                  </tr>
                ) : sortedColors.length === 0 ? (
                  <tr>
                    <td colSpan={colorColCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                      Ningún resultado con la búsqueda actual.
                    </td>
                  </tr>
                ) : (
                  sortedColors.map((row) => {
                    const isSelected = Number(selectedColorId) === Number(row.id);
                    return (
                    <tr
                      key={row.id}
                      style={{
                        borderBottom: `1px solid ${border}`,
                        background: isSelected ? ui.active : 'transparent',
                        cursor: 'pointer',
                      }}
                      onClick={() => setSelectedColorId((current) => (Number(current) === Number(row.id) ? null : row.id))}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = rowHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = isSelected ? ui.active : 'transparent';
                      }}
                    >
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => setSelectedColorId((current) => (Number(current) === Number(row.id) ? null : row.id))}
                          onClick={(event) => event.stopPropagation()}
                          aria-label={`Seleccionar ${row.color_code || row.color_name || 'color'}`}
                        />
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontWeight: 700, fontSize: '12px', color: text }}>
                        {row.color_code || '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', fontSize: '12px', color: muted }}>
                        {row.family_digit ?? '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text }}>
                        {row.family_name || '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text, maxWidth: '240px' }}>
                        {row.color_name || '—'}
                      </td>
                      <td style={{ padding: `${padV}px ${padH}px` }}>{renderHexCell(row.hex_value)}</td>
                      <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                        {renderStatusBadge(row.status)}
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : workspaceTab === 'formats' ? (
        <div style={{ padding: '0 0 12px', background: bg }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              alignItems: 'center',
              padding: '10px 16px',
              borderBottom: `1px solid ${border}`,
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 700, color: text }}>
              Categorías de formato
            </span>
            <button
              type="button"
              onClick={() => {
                loadFormatCategories();
                loadFormats();
              }}
              disabled={formatCategoriesLoading || formatsLoading}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontSize: '12px',
                fontWeight: 600,
                cursor: formatCategoriesLoading || formatsLoading ? 'wait' : 'pointer',
              }}
            >
              Actualizar
            </button>
          </div>
          <div style={{ overflowX: 'auto', padding: '0 16px 12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '520px' }}>
              <thead>
                <tr style={{ background: headerBg }}>
                  {['Letra', 'Categoría', 'Estado', 'Acciones'].map((label) => (
                    <th
                      key={label}
                      style={{
                        textAlign: label === 'Estado' ? 'center' : label === 'Acciones' ? 'right' : 'left',
                        padding: `${thPadV}px ${padH + 4}px`,
                        fontSize: '11px',
                        fontWeight: 700,
                        color: muted,
                        textTransform: 'uppercase',
                        borderBottom: `1px solid ${border}`,
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {formatCategoriesLoading ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: muted }}>
                      Cargando categorías…
                    </td>
                  </tr>
                ) : formatCategoriesOrdered.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: '16px', textAlign: 'center', color: muted }}>
                      No hay categorías. Crea Volumen, Masa, Cantidad, etc.
                    </td>
                  </tr>
                ) : (
                  formatCategoriesOrdered.map((category) => {
                    const isActive = Number(category.is_active) === 1;
                    return (
                      <tr key={category.id} style={{ borderBottom: `1px solid ${border}` }}>
                        <td style={{ padding: `${padV}px ${padH}px`, fontWeight: 800, fontSize: '13px', color: '#2563eb' }}>
                          {category.category_code}
                        </td>
                        <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text }}>
                          {category.display_name}
                        </td>
                        <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                          {renderStatusBadge(isActive ? 'Activo' : 'Inactivo')}
                        </td>
                        <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', whiteSpace: 'nowrap' }}>
                          <button
                            type="button"
                            onClick={() => openEditFormatCategory(category)}
                            title="Editar"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: `1px solid ${border}`,
                              background: inputBg,
                              color: text,
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              marginRight: '6px',
                            }}
                          >
                            <Pencil size={14} aria-hidden />
                            Editar
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteFormatCategory(category)}
                            title="Eliminar"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: '1px solid #fecaca',
                              background: isDarkMode ? '#450a0a' : '#fef2f2',
                              color: '#b91c1c',
                              fontSize: '11px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            <Trash2 size={14} aria-hidden />
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              alignItems: 'center',
              padding: '10px 16px',
              borderBottom: `1px solid ${border}`,
              gap: '8px',
              marginTop: '8px',
            }}
          >
            <span style={{ fontSize: '12px', fontWeight: 700, color: text }}>
              Formatos (SKU de 2 letras: categoría + correlativo)
            </span>
            <button
              type="button"
              onClick={() => loadFormats()}
              disabled={formatsLoading}
              style={{
                height: '36px',
                padding: '0 12px',
                borderRadius: '8px',
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontSize: '12px',
                fontWeight: 600,
                cursor: formatsLoading ? 'wait' : 'pointer',
              }}
            >
              Actualizar lista
            </button>
          </div>
          <div style={{ overflowX: 'auto', padding: '0 16px 12px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '680px' }}>
              <thead>
                <tr style={{ background: headerBg }}>
                  <th
                    style={{
                      width: '42px',
                      padding: `${thPadV}px ${padH}px`,
                      borderBottom: `1px solid ${border}`,
                    }}
                  />
                  {['Código', 'Categoría', 'Nombre', 'Orden', 'Estado'].map((label) => (
                    <th
                      key={label}
                      style={{
                        textAlign:
                          label === 'Orden'
                            ? 'right'
                            : label === 'Estado'
                              ? 'center'
                              : label === 'Acciones'
                                ? 'right'
                                : 'left',
                        padding: `${thPadV}px ${padH + 4}px`,
                        fontSize: '11px',
                        fontWeight: 700,
                        color: muted,
                        textTransform: 'uppercase',
                        borderBottom: `1px solid ${border}`,
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {formatsLoading ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: muted }}>
                      Cargando formatos…
                    </td>
                  </tr>
                ) : formatsRowsOrdered.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '20px', textAlign: 'center', color: muted }}>
                      No hay formatos. Usa «Nuevo formato» para agregarlos.
                    </td>
                  </tr>
                ) : (
                  formatsRowsOrdered.map((f) => {
                    const isActive = Number(f.is_active) === 1;
                    const isSelected = Number(selectedFormatId) === Number(f.id);
                    return (
                      <tr
                        key={f.id}
                        style={{
                          borderBottom: `1px solid ${border}`,
                          background: isSelected ? ui.active : 'transparent',
                          cursor: 'pointer',
                        }}
                        onClick={() => setSelectedFormatId((current) => (Number(current) === Number(f.id) ? null : f.id))}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = rowHover;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = isSelected ? ui.active : 'transparent';
                        }}
                      >
                        <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => setSelectedFormatId((current) => (Number(current) === Number(f.id) ? null : f.id))}
                            onClick={(event) => event.stopPropagation()}
                            aria-label={`Seleccionar ${f.format_code || f.display_name || 'formato'}`}
                          />
                        </td>
                        <td
                          style={{
                            padding: `${padV}px ${padH}px`,
                            fontWeight: 700,
                            fontSize: '12px',
                            color: text,
                          }}
                        >
                          {f.format_code}
                        </td>
                        <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: muted }}>
                          {formatCategoryLabel(f)}
                        </td>
                        <td style={{ padding: `${padV}px ${padH}px`, fontSize: '12px', color: text }}>
                          {f.display_name}
                        </td>
                        <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'right', fontSize: '12px', color: muted }}>
                          {f.sort_order ?? '—'}
                        </td>
                        <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                          {renderStatusBadge(isActive ? 'Activo' : 'Inactivo')}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <Dialog
        isOpen={showProductModal}
        onClose={() => !saveProductLoading && setShowProductModal(false)}
        title="Nuevo producto T-COAT"
        subtitle="Catálogo de pintura, formatos y costos por variante."
        size="lg"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveProductLoading}
        closeOnEscape={!saveProductLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>SKU *</span>
            <input
              type="text"
              value={newProductSku}
              onChange={(e) => setNewProductSku(e.target.value.toUpperCase())}
              disabled={saveProductLoading}
              placeholder="Ej. TC-LAT-001"
              maxLength={32}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre del producto *</span>
            <input
              type="text"
              value={newProductName}
              onChange={(e) => setNewProductName(e.target.value)}
              disabled={saveProductLoading}
              placeholder="Ej. Látex Interior Premium"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>
              Imagen del producto (opcional)
            </span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleProductImageChange}
              disabled={saveProductLoading}
              style={{
                ...selectStyle,
                width: '100%',
                fontFamily: 'inherit',
                padding: '6px 10px',
                height: 'auto',
              }}
            />
            <span style={{ fontSize: '11px', color: muted }}>
              JPEG, PNG o WebP · máximo 2 MB
            </span>
            {newProductImagePreview ? (
              <div
                style={{
                  marginTop: '4px',
                  border: `1px solid ${border}`,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: headerBg,
                  maxHeight: '160px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={newProductImagePreview}
                  alt="Vista previa"
                  style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain' }}
                />
              </div>
            ) : null}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>
              Formatos, colores y costos
            </span>
            <ProductCostLinesTable
              lines={newProductCostLines}
              onAddLine={addNewProductCostLine}
              onUpdateLine={updateNewProductCostLine}
              onRemoveLine={removeNewProductCostLine}
              formats={formats}
              colors={colors}
              productSku={newProductSku}
              disabled={saveProductLoading}
              border={border}
              muted={muted}
              text={text}
              inputBg={inputBg}
              selectStyle={selectStyle}
            />
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Estado</span>
            <select
              value={newProductStatus}
              onChange={(e) => setNewProductStatus(e.target.value)}
              disabled={saveProductLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            >
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
            </select>
          </label>
          {productFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{productFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveProductLoading}
              onClick={() => setShowProductModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveProductLoading}
              onClick={handleSaveProduct}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveProductLoading ? 'Guardando…' : 'Guardar producto'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showEditProductModal}
        onClose={() => !saveEditProductLoading && setShowEditProductModal(false)}
        title="Editar producto T-COAT"
        subtitle="Actualiza datos del producto y sus líneas de costo por formato/color."
        size="lg"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveEditProductLoading}
        closeOnEscape={!saveEditProductLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>SKU *</span>
            <input
              type="text"
              value={editProductSku}
              onChange={(e) => setEditProductSku(e.target.value.toUpperCase())}
              disabled={saveEditProductLoading}
              maxLength={32}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre del producto *</span>
            <input
              type="text"
              value={editProductName}
              onChange={(e) => setEditProductName(e.target.value)}
              disabled={saveEditProductLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Imagen del producto</span>
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={handleEditProductImageChange}
              disabled={saveEditProductLoading}
              style={{
                ...selectStyle,
                width: '100%',
                fontFamily: 'inherit',
                padding: '6px 10px',
                height: 'auto',
              }}
            />
            <span style={{ fontSize: '11px', color: muted }}>
              JPEG, PNG o WebP · máximo 2 MB
            </span>
            {editProductExistingImagePath && !editProductImageFile ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
                <input
                  type="checkbox"
                  checked={editProductRemoveImage}
                  onChange={(e) => setEditProductRemoveImage(e.target.checked)}
                  disabled={saveEditProductLoading}
                />
                Quitar imagen actual
              </label>
            ) : null}
            {editProductImagePreview ? (
              <div
                style={{
                  marginTop: '4px',
                  border: `1px solid ${border}`,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: headerBg,
                  maxHeight: '160px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={editProductImagePreview}
                  alt="Vista previa"
                  style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain' }}
                />
              </div>
            ) : !editProductRemoveImage && editProductExistingImagePath ? (
              <div
                style={{
                  marginTop: '4px',
                  border: `1px solid ${border}`,
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: headerBg,
                  maxHeight: '160px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={resolveProductImageUrl(editProductExistingImagePath)}
                  alt="Imagen actual"
                  style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain' }}
                />
              </div>
            ) : null}
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>
              Formatos, colores y costos
            </span>
            <ProductCostLinesTable
              lines={editProductCostLines}
              onAddLine={addEditProductCostLine}
              onUpdateLine={updateEditProductCostLine}
              onRemoveLine={removeEditProductCostLine}
              formats={formats}
              colors={colors}
              productSku={editProductSku}
              disabled={saveEditProductLoading}
              border={border}
              muted={muted}
              text={text}
              inputBg={inputBg}
              selectStyle={selectStyle}
            />
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Estado</span>
            <select
              value={editProductStatus}
              onChange={(e) => setEditProductStatus(e.target.value)}
              disabled={saveEditProductLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            >
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
            </select>
          </label>
          {editProductFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{editProductFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveEditProductLoading}
              onClick={() => setShowEditProductModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveEditProductLoading}
              onClick={handleSaveEditProduct}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveEditProductLoading ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showColorModal}
        onClose={() => !saveColorLoading && setShowColorModal(false)}
        title="Nuevo color T-COAT"
        subtitle="Catálogo de colores (paint_colors)."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveColorLoading}
        closeOnEscape={!saveColorLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código *</span>
            <input
              type="text"
              value={newColorCode}
              onChange={(e) => setNewColorCode(e.target.value.toUpperCase())}
              disabled={saveColorLoading}
              placeholder="Ej. RAL9010"
              maxLength={24}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre del color *</span>
            <input
              type="text"
              value={newColorName}
              onChange={(e) => setNewColorName(e.target.value)}
              disabled={saveColorLoading}
              placeholder="Ej. Blanco tráfico"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Familia RAL</span>
            <select
              value={newColorFamilyDigit}
              onChange={(e) => setNewColorFamilyDigit(e.target.value)}
              disabled={saveColorLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            >
              {RAL_FAMILY_OPTIONS.map((f) => (
                <option key={f.digit} value={String(f.digit)}>
                  {f.digit} — {f.name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Hex (opcional)</span>
            <input
              type="text"
              value={newColorHex}
              onChange={(e) => setNewColorHex(e.target.value)}
              disabled={saveColorLoading}
              placeholder="#FFFFFF"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="checkbox"
              checked={newColorIsRal}
              onChange={(e) => setNewColorIsRal(e.target.checked)}
              disabled={saveColorLoading}
            />
            Es color RAL
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Estado</span>
            <select
              value={newColorStatus}
              onChange={(e) => setNewColorStatus(e.target.value)}
              disabled={saveColorLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            >
              <option value="Activo">Activo</option>
              <option value="Inactivo">Inactivo</option>
            </select>
          </label>
          {colorFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{colorFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveColorLoading}
              onClick={() => setShowColorModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveColorLoading}
              onClick={handleSaveColor}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveColorLoading ? 'Guardando…' : 'Guardar color'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showEditColorModal}
        onClose={() => !saveEditColorLoading && setShowEditColorModal(false)}
        title="Editar color"
        subtitle="Actualiza código, familia RAL, hex y estado."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveEditColorLoading}
        closeOnEscape={!saveEditColorLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código</span>
            <input
              type="text"
              value={editColorCode}
              onChange={(e) => setEditColorCode(e.target.value.toUpperCase())}
              disabled={saveEditColorLoading}
              maxLength={24}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre</span>
            <input
              type="text"
              value={editColorName}
              onChange={(e) => setEditColorName(e.target.value)}
              disabled={saveEditColorLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Familia RAL</span>
            <select
              value={editColorFamilyDigit}
              onChange={(e) => setEditColorFamilyDigit(e.target.value)}
              disabled={saveEditColorLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            >
              {RAL_FAMILY_OPTIONS.map((f) => (
                <option key={f.digit} value={String(f.digit)}>
                  {f.digit} — {f.name}
                </option>
              ))}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Hex</span>
            <input
              type="text"
              value={editColorHex}
              onChange={(e) => setEditColorHex(e.target.value)}
              disabled={saveEditColorLoading}
              placeholder="#FFFFFF"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="checkbox"
              checked={editColorIsRal}
              onChange={(e) => setEditColorIsRal(e.target.checked)}
              disabled={saveEditColorLoading}
            />
            Es color RAL
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="checkbox"
              checked={editColorIsActive}
              onChange={(e) => setEditColorIsActive(e.target.checked)}
              disabled={saveEditColorLoading}
            />
            Color activo
          </label>
          {editColorFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{editColorFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveEditColorLoading}
              onClick={() => setShowEditColorModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveEditColorLoading}
              onClick={handleSaveEditColor}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveEditColorLoading ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showFormatModal}
        onClose={() => !saveFormatLoading && setShowFormatModal(false)}
        title="Nuevo formato T-COAT"
        subtitle="El código de 2 letras se asigna automáticamente según la categoría."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveFormatLoading}
        closeOnEscape={!saveFormatLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Categoría *</span>
            <select
              value={newFormatCategoryId}
              onChange={(e) => setNewFormatCategoryId(e.target.value)}
              disabled={saveFormatLoading || activeFormatCategories.length === 0}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            >
              <option value="">Selecciona categoría…</option>
              {activeFormatCategories.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.category_code} — {c.display_name}
                </option>
              ))}
            </select>
          </label>
          <div
            style={{
              padding: '10px 12px',
              borderRadius: '8px',
              border: `1px solid ${border}`,
              background: inputBg,
              fontSize: '12px',
              color: muted,
            }}
          >
            Código asignado:{' '}
            <strong style={{ color: '#2563eb', fontSize: '14px', letterSpacing: '0.06em' }}>
              {newFormatCodePreview || '—'}
            </strong>
            <div style={{ marginTop: '4px', fontSize: '11px' }}>
              1ª letra = categoría · 2ª letra = correlativo (A, B, C…)
            </div>
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre *</span>
            <input
              type="text"
              value={newFormatName}
              onChange={(e) => setNewFormatName(e.target.value)}
              disabled={saveFormatLoading}
              placeholder="Ej. Galón, Lata ¼, Saco 25 kg"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          {formatFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{formatFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveFormatLoading}
              onClick={() => setShowFormatModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveFormatLoading}
              onClick={handleSaveFormat}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveFormatLoading ? 'Guardando…' : 'Guardar formato'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showEditFormatModal}
        onClose={() => !saveEditFormatLoading && setShowEditFormatModal(false)}
        title="Editar formato"
        subtitle="El código no se puede cambiar en formatos categorizados."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveEditFormatLoading}
        closeOnEscape={!saveEditFormatLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          {editFormatHasCategory ? (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Categoría</span>
                <input
                  type="text"
                  value={editFormatCategoryLabel}
                  disabled
                  style={{ ...selectStyle, width: '100%', fontFamily: 'inherit', opacity: 0.85 }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código</span>
                <input
                  type="text"
                  value={editFormatCode}
                  disabled
                  style={{ ...selectStyle, width: '100%', fontFamily: 'inherit', opacity: 0.85 }}
                />
              </label>
            </>
          ) : (
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Código (legado)</span>
              <input
                type="text"
                value={editFormatCode}
                onChange={(e) => setEditFormatCode(e.target.value.toUpperCase())}
                disabled={saveEditFormatLoading}
                maxLength={12}
                style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
              />
            </label>
          )}
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre visible</span>
            <input
              type="text"
              value={editFormatName}
              onChange={(e) => setEditFormatName(e.target.value)}
              disabled={saveEditFormatLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Orden (sort_order)</span>
            <input
              type="number"
              step={1}
              value={editFormatSortOrder}
              onChange={(e) => setEditFormatSortOrder(e.target.value)}
              disabled={saveEditFormatLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="checkbox"
              checked={editFormatIsActive}
              onChange={(e) => setEditFormatIsActive(e.target.checked)}
              disabled={saveEditFormatLoading}
            />
            Formato activo
          </label>
          {editFormatFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{editFormatFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveEditFormatLoading}
              onClick={() => setShowEditFormatModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveEditFormatLoading}
              onClick={handleSaveEditFormat}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveEditFormatLoading ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showFormatCategoryModal}
        onClose={() => !saveFormatCategoryLoading && setShowFormatCategoryModal(false)}
        title="Nueva categoría de formato"
        subtitle="Una letra identifica el tipo: Volumen, Masa, Cantidad…"
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveFormatCategoryLoading}
        closeOnEscape={!saveFormatCategoryLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Letra *</span>
            <input
              type="text"
              value={newFormatCategoryCode}
              onChange={(e) => setNewFormatCategoryCode(e.target.value.toUpperCase().slice(0, 1))}
              disabled={saveFormatCategoryLoading}
              placeholder="Ej. V, M, C"
              maxLength={1}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre *</span>
            <input
              type="text"
              value={newFormatCategoryName}
              onChange={(e) => setNewFormatCategoryName(e.target.value)}
              disabled={saveFormatCategoryLoading}
              placeholder="Ej. Volumen, Masa, Cantidad"
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          {formatCategoryFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{formatCategoryFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveFormatCategoryLoading}
              onClick={() => setShowFormatCategoryModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveFormatCategoryLoading}
              onClick={handleSaveFormatCategory}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#16a34a',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveFormatCategoryLoading ? 'Guardando…' : 'Guardar categoría'}
            </button>
          </div>
        </div>
      </Dialog>

      <Dialog
        isOpen={showEditFormatCategoryModal}
        onClose={() => !saveEditFormatCategoryLoading && setShowEditFormatCategoryModal(false)}
        title="Editar categoría"
        subtitle="La letra de categoría no se puede cambiar."
        size="sm"
        isDarkMode={isDarkMode}
        zIndex={10050}
        closeOnOverlayClick={!saveEditFormatCategoryLoading}
        closeOnEscape={!saveEditFormatCategoryLoading}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Letra</span>
            <input
              type="text"
              value={editFormatCategoryCode}
              disabled
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit', opacity: 0.85 }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>Nombre</span>
            <input
              type="text"
              value={editFormatCategoryName}
              onChange={(e) => setEditFormatCategoryName(e.target.value)}
              disabled={saveEditFormatCategoryLoading}
              style={{ ...selectStyle, width: '100%', fontFamily: 'inherit' }}
            />
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: text }}>
            <input
              type="checkbox"
              checked={editFormatCategoryIsActive}
              onChange={(e) => setEditFormatCategoryIsActive(e.target.checked)}
              disabled={saveEditFormatCategoryLoading}
            />
            Categoría activa
          </label>
          {editFormatCategoryFormError ? (
            <p style={{ margin: 0, fontSize: '12px', color: '#b91c1c' }}>{editFormatCategoryFormError}</p>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' }}>
            <button
              type="button"
              disabled={saveEditFormatCategoryLoading}
              onClick={() => setShowEditFormatCategoryModal(false)}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: `1px solid ${border}`,
                background: inputBg,
                color: text,
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saveEditFormatCategoryLoading}
              onClick={handleSaveEditFormatCategory}
              style={{
                height: '36px',
                padding: '0 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              {saveEditFormatCategoryLoading ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PaintProductsTable3;
